import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';

import CountUp from 'react-countup';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiConfig';
import { claimCoupon, getCouponErrorMessage } from '../../api/couponService';
import { clearPendingFreeTrial } from '../../utils/freeTrialPending';

import Footer from '../../components/layout/Footer';
import useScrollReveal from '../../hooks/useScrollReveal';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import FreeTrialCTAButton from '../../components/cta/FreeTrialCTAButton';
import HwpDemoSection from '../../components/landing/HwpDemoSection';
import { SaenggibuResultDemo, ClassFlowDemo, ScoringScanDemo, PptEditDemo, ChatAnswerDemo, trackDemoCta } from '../../components/demo';
import { AI_MODELS } from '../../constants/aiModels';
import { getMaxDiscountLabel } from '../../config/seasonConfig';
import { 
  trackHomePreorderClick
} from '../../utils/eventTracker';
// GA4 전환 트래킹용
import { trackABTestEvent } from '../../utils/abTest';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

// 토스 철학 기반 홈페이지 전용 스타일 - Simple is the Best
const tossStyleHomepage = `
  /* FAQ 애니메이션 */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* 앰버서더 스크롤 컨테이너 - 스크롤바 숨기기 */
  .ambassador-scroll-container::-webkit-scrollbar {
    display: none;
  }

  /* 모바일 스티키 하단 CTA - 데스크톱에서는 숨김 */
  .home-sticky-cta {
    display: none;
  }

  @media (max-width: 768px) {
    .home-sticky-cta {
      display: block;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 90;
      padding: 12px 20px max(12px, env(safe-area-inset-bottom));
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-top: 1px solid #f2f4f6;
    }

    .home-sticky-cta.is-hidden {
      display: none;
    }

    .home-sticky-cta-btn {
      width: 100%;
      min-height: 52px;
      padding: 15px 24px;
      background: #3182f6;
      color: #fff;
      border: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(49, 130, 246, 0.3);
      transition: background 0.2s;
    }

    .home-sticky-cta-btn:active {
      background: #1b64da;
    }

    .home-sticky-cta-btn:disabled {
      background: #93c5fd;
      cursor: not-allowed;
    }
  }

  /* ── 앰버서더 2기: 공식 위촉 — 왓퀴즈 기본 톤(강조색 #3182f6 하나, 연회색 카드) ── */
  .home-ambassador-section__eyebrow {
    margin: 0 0 8px;
    text-align: center;
    color: #3182f6;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
  }

  .home-ambassador-card {
    flex: 0 0 168px;
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-height: 228px;
    padding: 16px 14px;
    border-radius: 16px;
    background: #f8f9fa;
    text-align: center;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.15s ease;
  }

  .home-ambassador-card:hover {
    transform: translateY(-2px);
    background: #f2f4f6;
  }

  .home-ambassador-card__label {
    display: inline-flex;
    align-items: center;
    min-height: 20px;
    padding: 0 8px;
    margin-bottom: 10px;
    color: #3182f6;
    background: #e8f3ff;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .home-ambassador-card__avatar {
    width: 64px;
    height: 64px;
    margin-bottom: 10px;
    object-fit: contain;
    background: transparent;
  }

  .home-ambassador-card__name {
    margin: 0 0 2px;
    color: #191f28;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .home-ambassador-card__school {
    margin: 0 0 10px;
    color: #8b95a1;
    font-size: 12px;
    font-weight: 500;
    word-break: keep-all;
  }

  .home-ambassador-card__thanks {
    margin: auto 0 0;
    padding-top: 10px;
    width: 100%;
    border-top: 1px solid #e5e8eb;
    color: #4e5968;
    font-size: 12px;
    line-height: 1.5;
    font-weight: 500;
    word-break: keep-all;
  }

  /* 모달 — 위촉장 구조, 기존 모달과 같은 톤 */
  .home-ambassador-modal__badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 0 10px;
    margin-bottom: 8px;
    color: #3182f6;
    background: #e8f3ff;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .home-ambassador-modal__title {
    margin: 0 0 18px;
    color: #4e5968;
    font-size: 14px;
    font-weight: 600;
  }

  .home-ambassador-modal__avatar {
    display: block;
    width: 120px;
    height: 120px;
    margin: 0 auto 14px;
    object-fit: contain;
    background: #f9fafb;
    border-radius: 16px;
  }

  .home-ambassador-modal__name {
    margin: 0 0 4px;
    color: #191f28;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .home-ambassador-modal__school {
    margin: 0;
    color: #8b95a1;
    font-size: 14px;
    font-weight: 500;
  }

  .home-ambassador-modal__rule {
    width: 100%;
    height: 1px;
    margin: 18px 0 14px;
    border: 0;
    background: #f2f4f6;
  }

  .home-ambassador-modal__appoint {
    margin: 0;
    color: #333d4b;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.6;
    word-break: keep-all;
  }

  .home-ambassador-modal__label {
    display: block;
    margin: 20px 0 8px;
    color: #8b95a1;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
  }

  .home-ambassador-modal__thanks {
    padding: 16px 18px;
    background: #f9fafb;
    border-radius: 12px;
    text-align: left;
  }

  .home-ambassador-modal__thanks p {
    margin: 0;
    color: #4e5968;
    font-size: 14px;
    line-height: 1.7;
    word-break: keep-all;
  }

  .home-ambassador-modal__thanks p + p {
    margin-top: 10px;
  }

  .home-ambassador-modal__thanks small {
    display: block;
    margin-top: 12px;
    color: #8b95a1;
    font-size: 12px;
    font-weight: 600;
    text-align: right;
  }

  .home-coupon-modal__perks {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
    text-align: left;
  }

  .home-coupon-modal__perks li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    color: #333d4b;
    background: #f9fafb;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
  }

  .home-coupon-modal__perks svg {
    flex-shrink: 0;
  }

  /* 히어로 섹션 - 강렬한 훅 (타이포 스케일: AI30 랜딩 기준) */
  .toss-hero {
    text-align: center;
    padding: 40px 20px 48px;
    background: #fff;
    max-width: 860px;
    margin: 0 auto;
    word-break: keep-all;
  }

  .toss-hero-title {
    font-size: clamp(32px, 5vw, 50px);
    font-weight: 800;
    line-height: 1.18;
    color: #111827;
    margin-bottom: 16px;
    font-family: var(--whatquiz-font-family);
    letter-spacing: -0.04em;
  }

  .toss-hero-subtitle {
    font-size: clamp(16px, 2.2vw, 18px);
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 32px;
    line-height: 1.6;
  }

  .toss-hero-cta {
    background: var(--whatquiz-primary);
    color: white;
    border: none;
    padding: 16px 36px;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(49, 130, 246, 0.3);
  }

  .toss-hero-cta:hover {
    background: var(--whatquiz-primary-hover);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(49, 130, 246, 0.35);
  }

  /* 섹션 공통 (타이포 스케일: AI30 랜딩 기준) */
  .toss-section {
    padding: 48px 20px;
    max-width: 860px;
    margin: 0 auto;
    word-break: keep-all;
  }

  .toss-section-title {
    font-size: clamp(24px, 3.4vw, 32px);
    font-weight: 800;
    color: #111827;
    line-height: 1.25;
    margin-bottom: 10px;
    text-align: center;
    font-family: var(--whatquiz-font-family);
    letter-spacing: -0.035em;
  }

  .toss-section-desc {
    font-size: 16px;
    color: #6b7280;
    text-align: center;
    line-height: 1.65;
    max-width: 600px;
    margin: 0 auto 32px;
  }

  .toss-feature-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    max-width: 860px;
    margin: 0 auto;
  }

  .toss-feature-card {
    text-align: center;
    background: #f8f9fa;
    border: none;
    border-radius: 16px;
    padding: 32px 24px;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .toss-feature-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }

  .toss-feature-icon {
    width: 48px;
    height: 48px;
    background: #e8f3ff;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: #3182f6;
    box-shadow: 0 2px 8px rgba(49, 130, 246, 0.06);
  }

  .toss-feature-title {
    font-size: 18px;
    font-weight: 600;
    color: #191f28;
    margin-bottom: 8px;
    font-family: var(--whatquiz-font-family);
  }

  .toss-feature-desc {
    font-size: 14px;
    color: #6b7684;
    line-height: 1.5;
    margin-bottom: 0;
  }



  /* 스크롤 애니메이션 — AI30 랜딩 검증 스케일 (28px · 0.85s · expo-out) */
  .animate-section {
    opacity: 0;
    transform: translateY(var(--whatquiz-reveal-distance, 28px));
    transition:
      opacity var(--whatquiz-reveal-duration, 0.85s) var(--whatquiz-ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)),
      transform var(--whatquiz-reveal-duration, 0.85s) var(--whatquiz-ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1));
  }
  
  .animate-section.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .animate-card {
    opacity: 0;
    transform: translateY(24px);
    transition:
      opacity 0.7s var(--whatquiz-ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)),
      transform 0.7s var(--whatquiz-ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1));
  }
  
  .animate-card.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .animate-card:nth-child(1) { transition-delay: 0.08s; }
  .animate-card:nth-child(2) { transition-delay: 0.16s; }
  .animate-card:nth-child(3) { transition-delay: 0.24s; }

  /* 모션 최소화 — 리빌 즉시 표시 (JS 훅의 즉시 visible 부여와 이중 안전망) */
  @media (prefers-reduced-motion: reduce) {
    .animate-section,
    .animate-card,
    .animate-section .demo-text-side,
    .animate-section .demo-mockup-side {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
  }

  /* ── 데모 섹션 ── */
  @keyframes demoBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
  @keyframes demoDotPulse {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }

  .demo-mockup {
    max-width: 540px;
    margin: 24px auto 0;
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 4px 32px rgba(0,0,0,0.08);
    overflow: hidden;
  }
  .demo-mockup-bar {
    min-height: 44px;
    padding: 0 16px;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .demo-mockup-title {
    color: #4e5968;
    font-size: 13px;
    font-weight: 700;
  }
  .demo-mockup-body {
    padding: 24px;
  }
  .demo-cursor {
    display: inline-block;
    width: 2px;
    height: 1.1em;
    background: #3182f6;
    margin-left: 1px;
    vertical-align: text-bottom;
    animation: demoBlink 0.7s step-end infinite;
  }
  .demo-btn-row {
    text-align: right;
    padding: 12px 0 4px;
  }
  .demo-gen-btn {
    padding: 8px 20px;
    background: #3182f6;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.3s ease;
    pointer-events: none;
  }
  .demo-gen-btn.pressed {
    transform: scale(0.93);
    box-shadow: 0 2px 12px rgba(49, 130, 246, 0.4);
  }
  .demo-loading-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 20px 0;
  }
  .demo-loading-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #3182f6;
    animation: demoDotPulse 1s infinite;
  }
  .demo-loading-dot:nth-child(2) { animation-delay: 0.15s; }
  .demo-loading-dot:nth-child(3) { animation-delay: 0.3s; }
  .demo-cta-btn {
    padding: 14px 32px;
    background: #3182f6;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(49, 130, 246, 0.25);
  }
  .demo-cta-btn:hover {
    background: #1b64da;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(49, 130, 246, 0.35);
  }

  /* AI 한글(HWP) 데모는 HwpDemoSection 컴포넌트 내부 CSS 로 이동.
     ad-pro 등 다른 페이지에서도 동일하게 import 해 사용. */

  /* ── 검사기 데모 ── */
  @keyframes inspDetailIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .demo-insp-text {
    font-size: 14px;
    line-height: 1.8;
    color: #374151;
    word-break: keep-all;
    background: #f8f9fa;
    border-radius: 12px;
    padding: 14px 16px;
  }
  .demo-hl {
    padding: 1px 3px;
    border-radius: 3px;
    border-bottom: 2px solid transparent;
    background: transparent;
    transition: all 0.35s ease;
  }
  .demo-hl.on {
    cursor: default;
  }
  .demo-hl.on.brand {
    background: rgba(220, 38, 38, 0.12);
    border-bottom-color: #dc2626;
  }
  .demo-hl.on.ending {
    background: rgba(59, 130, 246, 0.12);
    border-bottom-color: #3b82f6;
  }
  .demo-hl.on.institution {
    background: rgba(234, 88, 12, 0.12);
    border-bottom-color: #ea580c;
  }
  .demo-hl.sel {
    font-weight: 600;
  }
  .demo-hl.sel.brand {
    background: rgba(220, 38, 38, 0.22);
  }
  .demo-hl.sel.ending {
    background: rgba(59, 130, 246, 0.22);
  }
  .demo-hl.sel.institution {
    background: rgba(234, 88, 12, 0.22);
  }
  .demo-hl.on.spelling {
    background: rgba(8, 145, 178, 0.12);
    border-bottom-color: #0891b2;
  }
  .demo-hl.on.expression {
    background: rgba(202, 138, 4, 0.12);
    border-bottom-color: #ca8a04;
  }
  .demo-hl.on.repetition {
    background: rgba(124, 58, 237, 0.12);
    border-bottom-color: #7c3aed;
  }
  .demo-hl.sel.spelling {
    background: rgba(8, 145, 178, 0.22);
  }
  .demo-hl.sel.expression {
    background: rgba(202, 138, 4, 0.22);
  }
  .demo-hl.sel.repetition {
    background: rgba(124, 58, 237, 0.22);
  }
  .demo-insp-summary {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #dc2626;
    opacity: 0;
    transform: translateY(6px);
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .demo-insp-summary.show {
    opacity: 1;
    transform: translateY(0);
  }
  .demo-insp-detail {
    margin-top: 14px;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 14px;
    padding: 16px;
    animation: inspDetailIn 0.4s cubic-bezier(0.32, 0.72, 0, 1) forwards;
  }
  .demo-insp-detail-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
  }
  .demo-insp-cat {
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    color: white;
  }
  .demo-insp-fix {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 13px;
    margin-bottom: 10px;
  }
  .demo-insp-fix-from {
    text-decoration: line-through;
    color: #dc2626;
    font-weight: 500;
  }
  .demo-insp-fix-to {
    color: #059669;
    font-weight: 600;
  }
  .demo-insp-reason {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 8px;
  }
  .demo-insp-source {
    font-size: 12px;
    color: #9ca3af;
    padding: 8px 10px;
    background: #fafbfc;
    border-radius: 8px;
    border-left: 3px solid #e5e7eb;
    line-height: 1.6;
  }
  .demo-insp-source strong {
    color: #6b7280;
    display: block;
    margin-bottom: 2px;
  }

  /* ── 스크롤 Parallax + 페이드업 (AI30 텍스트→목업 스태거 리듬) ── */
  .demo-mockup-side {
    transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.9s ease;
  }
  .animate-section .demo-text-side {
    opacity: 0;
    transform: translateY(28px);
    transition: all 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.05s;
  }
  .animate-section.visible .demo-text-side {
    opacity: 1;
    transform: translateY(0);
  }
  .animate-section .demo-mockup-side {
    opacity: 0;
    transform: translateY(36px);
    transition: all 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.16s;
  }
  .animate-section.visible .demo-mockup-side {
    opacity: 1;
    transform: translateY(0);
  }

  /* ── 좌우 교대 레이아웃 ── */
  .demo-row {
    display: flex;
    align-items: center;
    gap: 40px;
    max-width: 860px;
    margin: 0 auto;
  }
  .demo-row-reverse {
    flex-direction: row-reverse;
  }
  .demo-row .demo-text-side {
    flex: 1;
    min-width: 0;
  }
  .demo-row .demo-mockup-side {
    flex: 1.2;
    min-width: 0;
  }
  .demo-row .demo-mockup {
    margin: 0;
  }
  .demo-text-title {
    font-size: clamp(22px, 3vw, 28px);
    font-weight: 800;
    color: #111827;
    line-height: 1.28;
    letter-spacing: -0.03em;
    margin-bottom: 10px;
    font-family: var(--whatquiz-font-family);
  }
  .demo-text-desc {
    font-size: 15px;
    color: #6b7684;
    line-height: 1.7;
  }

  /* 반응형 */
  @media (max-width: 768px) {
    .toss-hero {
      padding: 40px 20px 32px;
    }

    /* 히어로·섹션 제목 크기는 clamp()가 반응형을 담당한다 */

    .toss-section {
      padding: 36px 16px;
    }
    
    .toss-feature-grid {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      gap: 12px;
      padding: 4px 4px 16px;
      scrollbar-width: none;
    }

    .toss-feature-grid::-webkit-scrollbar { display: none; }

    .toss-feature-card {
      min-width: 260px;
      flex-shrink: 0;
      scroll-snap-align: center;
      padding: 24px 20px;
    }

    .demo-mockup-body {
      padding: 16px;
    }
    .demo-row, .demo-row.demo-row-reverse {
      flex-direction: column;
      gap: 20px;
    }
    .demo-text-side {
      text-align: center;
    }
    /* .demo-text-title 크기는 clamp()가 담당 */
    .demo-text-desc {
      font-size: 14px;
      margin: 0;
    }
    .home-prep-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
      padding: 0 !important;
    }
    .home-prep-card-desc { display: none; }
    .home-prep-card-body { padding: 10px 10px 12px !important; }
    .home-prep-card-title { font-size: 13px !important; margin: 0 !important; }
  }


  .home-demo-solo {
    max-width: 560px;
    margin: 0 auto;
  }
  .home-demo-cta {
    text-align: center;
    margin-top: 24px;
  }
  .home-demo-link {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    margin-top: 8px;
    padding: 0;
    border: none;
    background: none;
    color: #3182f6;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
  }
  .home-demo-link:hover { text-decoration: underline; }
  .home-demo-link:focus-visible { outline: 2px solid #3182f6; outline-offset: 2px; border-radius: 4px; }
  @media (max-width: 768px) {
    .home-demo-link { justify-content: center; }
  }

  .home-hover-lift:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }
  .home-btn-primary {
    background: #3182f6;
  }
  .home-btn-primary:hover:not(:disabled) {
    background: #1b64da;
    transform: translateY(-1px);
  }
  .home-btn-primary:disabled {
    background: #93c5fd;
  }
  .home-btn-primary--lift:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(49, 130, 246, 0.3);
  }
  .home-btn-soft {
    background: #f3f4f6;
  }
  .home-btn-soft:hover {
    background: #e5e7eb;
  }
  .home-btn-ghost {
    color: #6b7684;
  }
  .home-btn-ghost:hover {
    color: #3182f6;
  }
`;


const INSP_TEXT = '수업시간에  성실하게 참여하였으며 서울대학교 진학을 목표로 다양한 활동에 임했다. 영어 듣기 능력이 뛰어나 유창하게말하며 과제를 함께 해결했다. 학교 체육 활동에서 나이키 운동화를 신고적극적으로 참여하였고, 열심히 노력하여 좋은 성과를 거두었다. 매우 매우 성실하며 친구들과 협력을  잘함. 어려운 과제도 끝까지 완수하고싶어졌으며, 학셍의 학습 방법을 스스로 개선하려는 태도를 보였음.';
const INSP_VIOLATIONS = [
  { found: '수업시간에  성실하게', cat: 'spelling', color: '#0891b2', type: '중복 공백', suggestion: '수업시간에 성실하게', reason: '연속된 공백 2개 이상은 NEIS 입력 시 오류 원인이 되므로 공백 1개로 수정해야 합니다.', source: '실제 검사기 응답', quote: '연속된 공백 2개 이상은 NEIS 입력 시 오류 원인이 되므로 반드시 공백 1개로 수정해야 함.' },
  { found: '참여하였으며', cat: 'ending', color: '#3b82f6', type: '과거형 연결어미', suggestion: '참여하며', reason: '생기부 서술형은 현재형 연결어미 사용이 원칙입니다.', source: '실제 검사기 응답', quote: '과거형 연결어미는 현재형 연결어미로 수정해야 함.' },
  { found: '서울대학교', cat: 'institution', color: '#ea580c', type: '특정 대학명 기재 금지', suggestion: '상급 학교', reason: '구체적인 특정 대학명은 사교육 유발 요인이 될 수 있어 학교생활기록부에 입력하지 않도록 안내됩니다.', source: '2026학년도 학교생활기록부 기재요령(초등학교).pdf p.28', quote: '구체적인 특정 대학명, 기관명, 상호명, 강사명 등은 기재할 수 없음.' },
  { found: '임했다', cat: 'ending', color: '#3b82f6', type: '과거형 종결어미', suggestion: '임함', reason: '서술형 과거 시제 어미는 명사형 종결어미로 마무리해야 합니다.', source: '실제 검사기 응답', quote: '생기부는 명사형 종결어미 사용이 필수임.' },
  { found: '유창하게말하며', cat: 'spelling', color: '#0891b2', type: '띄어쓰기 오류', suggestion: '유창하게 말하며', reason: "부사 '유창하게'와 동사 '말하며'는 서로 다른 단어이므로 사이에 공백을 두어야 합니다.", source: '실제 검사기 응답', quote: "부사 '유창하게'와 동사 '말하며'는 서로 다른 단어이므로 사이에 공백을 두어야 함." },
  { found: '해결했다', cat: 'ending', color: '#3b82f6', type: '과거형 종결어미', suggestion: '해결함', reason: '서술형 과거 시제 어미는 명사형 종결어미로 수정하는 것이 적절합니다.', source: '실제 검사기 응답', quote: '서술형 과거 시제 어미는 생기부에서 지양하고, 명사형 종결어미로 마무리해야 함.' },
  { found: '나이키', cat: 'brand', color: '#dc2626', type: '브랜드/상호명 기재 금지', suggestion: '운동화', reason: '특정 브랜드는 사교육 유발 및 광고 효과가 있을 수 있어 기재 금지 대상입니다.', source: '2026학년도 학교생활기록부 기재요령(초등학교).pdf p.106', quote: '구체적인 특정 대학명, 기관명, 상호명, 강사명은 입력하지 않음.' },
  { found: '신고적극적으로', cat: 'spelling', color: '#0891b2', type: '띄어쓰기 오류', suggestion: '신고 적극적으로', reason: "동사 '신고'와 부사 '적극적으로'는 서로 다른 단어이므로 사이에 공백을 두어야 합니다.", source: '실제 검사기 응답', quote: "동사 '신고'와 부사 '적극적으로'는 서로 다른 단어이므로 사이에 공백을 두어야 함." },
  { found: '참여하였고', cat: 'ending', color: '#3b82f6', type: '과거형 연결어미', suggestion: '참여하고', reason: '과거형 연결어미는 현재형 연결어미로 수정하는 것이 적절합니다.', source: '실제 검사기 응답', quote: '과거형 연결어미는 현재형 연결어미로 수정해야 함.' },
  { found: '거두었다', cat: 'ending', color: '#3b82f6', type: '과거형 종결어미', suggestion: '거둠', reason: '서술형 과거 시제 어미는 명사형 종결어미로 마무리해야 합니다.', source: '실제 검사기 응답', quote: '서술형 과거 시제 어미는 생기부에서 지양하고, 명사형 종결어미로 마무리해야 함.' },
  { found: '매우 매우', cat: 'repetition', color: '#7c3aed', type: '동어 반복', suggestion: '매우', reason: '같은 단어를 연속 반복하는 것은 어색하고 불필요합니다.', source: '실제 검사기 응답', quote: '같은 단어를 연속 반복하는 것은 어색하고 불필요함.' },
  { found: '협력을  잘함', cat: 'spelling', color: '#0891b2', type: '중복 공백', suggestion: '협력을 잘함', reason: '연속된 공백 2개 이상은 NEIS 입력 시 오류 원인이 되므로 공백 1개로 수정해야 합니다.', source: '실제 검사기 응답', quote: '연속된 공백 2개 이상은 NEIS 입력 시 오류 원인이 되므로 반드시 공백 1개로 수정해야 함.' },
  { found: '완수하고싶어졌으며', cat: 'expression', color: '#ca8a04', type: '내면 감정/의지 표현', suggestion: '완수하며', reason: '학생의 내면적 감정이나 의지는 관찰 불가능한 영역이므로 객관적인 행동 중심으로 서술해야 합니다.', source: '실제 검사기 응답', quote: '관찰 불가능한 내면 표현은 객관적인 행동 중심으로 서술해야 함.' },
  { found: '학셍', cat: 'spelling', color: '#0891b2', type: '오타', suggestion: '학생', reason: '명백한 오탈자는 올바른 표기로 수정해야 합니다.', source: '실제 검사기 응답', quote: '명백한 오타는 수정해야 함.' },
];
const INSP_SEGS = (() => {
  const segs = [];
  const occurrences = INSP_VIOLATIONS
    .map((v, i) => ({ ...v, hi: i, start: INSP_TEXT.indexOf(v.found) }))
    .filter(v => v.start !== -1)
    .sort((a, b) => a.start - b.start);
  let last = 0;
  occurrences.forEach((v) => {
    if (v.start < last) return;
    if (v.start > last) segs.push({ t: INSP_TEXT.slice(last, v.start) });
    segs.push({ t: v.found, hi: v.hi });
    last = v.start + v.found.length;
  });
  if (last < INSP_TEXT.length) segs.push({ t: INSP_TEXT.slice(last) });
  return segs;
})();


// 앰버서더 2기 — 왓퀴즈 공식 위촉
// 마스킹 규칙: 이름은 가운데 글자 ○. 학교는 지역은 그대로 두고 "학교 고유명"의 두 번째 글자를 ○ 처리
// (예: 서울신동초등학교 → 서울 신○초등학교). 지역만 가리면 학교가 그대로 노출되어 의미가 없음.
// 2기는 각오 한마디를 받지 않았으므로, 카드/모달에는 왓퀴즈 팀이 선생님께 드리는 글을 싣는다.
const AMBASSADOR_GENERATION = 2;
const AMBASSADOR_APPOINT_YEAR = 2026;

const AMBASSADOR_ROSTER = [
  { id: 0, name: '김○현', school: '조치원 교○초등학교' },
  { id: 1, name: '김○은', school: '서울 신○초등학교' },
  { id: 2, name: '김○희', school: '고흥 풍○중학교' },
  { id: 3, name: '김○진', school: '서울 항○초등학교' },
  { id: 4, name: '김○지', school: '부산 당○초등학교' },
  { id: 5, name: '김○용', school: '충북 청○초등학교' },
  { id: 6, name: '김○영', school: '경기 곡○고등학교' },
  { id: 7, name: '김○진', school: '서울 영○초등학교' },
  { id: 8, name: '박○연', school: '서울 신○초등학교' },
  { id: 9, name: '박○웅', school: '전북 송○초등학교' },
  { id: 10, name: '성○연', school: '서울 동○문중학교' },
  { id: 11, name: '송○근', school: '인천 담○초등학교' },
  { id: 12, name: '신○철', school: '서울 구○초등학교' },
  { id: 13, name: '신○영', school: '충북 감○초등학교' },
  { id: 14, name: '심○연', school: '서울 송○초등학교' },
  { id: 15, name: '안○환', school: '창원 안○초등학교' },
  { id: 16, name: '이○비', school: '부산 연○초등학교' },
  { id: 17, name: '이○원', school: '원주 섬○초등학교' },
  { id: 18, name: '장○정', school: '서울 송○고등학교' },
  { id: 19, name: '정○하', school: '서울 광○방송예술고등학교' },
  { id: 20, name: '조○연', school: '서울 금○고등학교' },
  { id: 21, name: '조○아', school: '인천 가○초등학교' },
  { id: 22, name: '한○훈', school: '광주 금○중앙중학교' },
  { id: 23, name: '이○현', school: '경남 안○초등학교' },
  { id: 24, name: '김○주', school: '서울 홍○초등학교' },
];

// 카드용 한 줄 — "왓퀴즈가 선생님을 자랑스러워한다" + "선생님 덕분에 왓퀴즈가 자란다" 두 축만 사용
const AMBASSADOR_THANKS = [
  '왓퀴즈가 가장 자랑스러워하는 이름이에요',
  '선생님 덕분에 왓퀴즈가 한 걸음 더 자랐어요',
  '선생님의 이름을 이 자리에 올릴 수 있어 영광이에요',
  `전국에서 단 ${AMBASSADOR_ROSTER.length}분, 그중 한 분이에요`,
  '선생님의 교실이 있어 왓퀴즈가 자라요',
  '교실에서 먼저 길을 내주신 선생님, 존경해요',
  '선생님의 한마디가 왓퀴즈를 움직였어요',
  '선생님이 계셔서 왓퀴즈가 든든해요',
];

const AMBASSADORS = AMBASSADOR_ROSTER.map((amb) => ({
  ...amb,
  avatar: `/images/ambassadors/gen2/ambassador-${String(amb.id).padStart(2, '0')}.png`,
  thanks: AMBASSADOR_THANKS[amb.id % AMBASSADOR_THANKS.length],
}));

// 모달 편지 — 위촉의 무게(왓퀴즈의 자랑) → 감사(덕분에 성장) → 다짐
const buildAmbassadorLetter = (name, total) => [
  `${name} 선생님은 왓퀴즈가 공식으로 위촉한 앰버서더 ${AMBASSADOR_GENERATION}기, 전국에서 단 ${total}분 중 한 분이세요. 위촉장과 명함, 명찰에 담은 마음 그대로, 이 페이지에도 선생님의 이름을 자랑스럽게 올려요.`,
  '바쁜 교실 속에서도 먼저 왓퀴즈를 믿어주시고, 현장의 목소리를 들려주셔서 감사해요. 그 한마디 한마디가 기능이 되고, 다른 선생님의 저녁 시간이 됐어요. 선생님 덕분에 왓퀴즈가 자라고 있어요.',
  '선생님의 이름 옆에 서 있다는 게 왓퀴즈에게는 가장 큰 자랑이에요. 앞으로도 선생님이 자랑스러워할 수 있는 서비스로, 곁에서 함께 걸을게요.',
];

const SPRING14_COUPON_CODE = 'SPRING14';

const getPublicStatsUrl = () => {
  const explicitUrl = process.env.REACT_APP_PUBLIC_STATS_API_URL;
  if (explicitUrl) {
    return explicitUrl;
  }

  const statsApiBaseUrl = process.env.REACT_APP_PUBLIC_STATS_API_BASE_URL;
  if (statsApiBaseUrl) {
    return `${statsApiBaseUrl.replace(/\/$/, '')}/api/public-stats`;
  }

  return getApiUrl('/api/public-stats');
};

const HomePage = ({ isLandingPage = false }) => {
  // cutover 시점별 할인율 카피
  const maxDiscountLabel = getMaxDiscountLabel();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  const spring14ApplyingRef = useRef(false);
  const [isSpring14Applying, setIsSpring14Applying] = useState(false);
  const [showSpring14Guide, setShowSpring14Guide] = useState(false);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isSpring14CouponMode = searchParams.get('coupon')?.toUpperCase() === SPRING14_COUPON_CODE;

  // GA4 뷰 이벤트 추적 (페이지 로드 시 1회)
  useEffect(() => {
    trackABTestEvent('landing_cta_button', 'view', { isLandingPage });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 앰버서더 랜덤 셔플 (페이지 로드마다 순서 변경)
  const shuffledAmbassadors = useMemo(() => {
    const arr = [...AMBASSADORS];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);

  // 앰버서더 자동 슬라이드
  const ambassadorScrollRef = useRef(null);
  const ambassadorAutoScrollRef = useRef(null);
  const CARD_WIDTH = 180; // 168px card(.home-ambassador-card flex-basis) + 12px gap

  const startAutoScroll = useCallback(() => {
    if (ambassadorAutoScrollRef.current) return;
    ambassadorAutoScrollRef.current = setInterval(() => {
      const container = ambassadorScrollRef.current;
      if (!container) return;
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (container.scrollLeft >= maxScroll - 2) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: CARD_WIDTH, behavior: 'smooth' });
      }
    }, 3000);
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (ambassadorAutoScrollRef.current) {
      clearInterval(ambassadorAutoScrollRef.current);
      ambassadorAutoScrollRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoScroll();
    return () => stopAutoScroll();
  }, [startAutoScroll, stopAutoScroll]);

  // 앰버서더 PC 마우스 드래그 스크롤
  const ambassadorDragState = useRef({ isDown: false, startX: 0, scrollLeft: 0 });

  const handleDragStart = useCallback((e) => {
    const container = ambassadorScrollRef.current;
    if (!container) return;
    ambassadorDragState.current = { isDown: true, startX: e.pageX - container.offsetLeft, scrollLeft: container.scrollLeft };
    container.style.cursor = 'grabbing';
    container.style.scrollSnapType = 'none';
    stopAutoScroll();
  }, [stopAutoScroll]);

  const handleDragEnd = useCallback(() => {
    ambassadorDragState.current.isDown = false;
    const container = ambassadorScrollRef.current;
    if (container) {
      container.style.cursor = 'grab';
      setTimeout(() => { container.style.scrollSnapType = 'x mandatory'; }, 100);
    }
    setTimeout(startAutoScroll, 5000);
  }, [startAutoScroll]);

  const handleDragMove = useCallback((e) => {
    if (!ambassadorDragState.current.isDown) return;
    e.preventDefault();
    const container = ambassadorScrollRef.current;
    if (!container) return;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - ambassadorDragState.current.startX) * 1.5;
    container.scrollLeft = ambassadorDragState.current.scrollLeft - walk;
  }, []);

  // 앰버서더 모달 (Dialog 프리미티브 — 콘페티 없이 차분하게)
  const [selectedAmbassador, setSelectedAmbassador] = useState(null);
  const cardClickStart = useRef({ x: 0, y: 0 });

  const handleCardMouseDown = useCallback((e) => {
    cardClickStart.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleCardClick = useCallback((amb) => {
    const dx = Math.abs(cardClickStart.current.x - (window.event?.clientX ?? cardClickStart.current.x));
    const dy = Math.abs(cardClickStart.current.y - (window.event?.clientY ?? cardClickStart.current.y));
    if (dx < 5 && dy < 5) {
      setSelectedAmbassador(amb);
    }
  }, []);

  // 무료체험 모달 상태
  const [showProModal, setShowProModal] = useState(false);

  // 모바일 스티키 CTA: 히어로 CTA·마무리 CTA·푸터가 보일 땐 숨긴다 (AI30 페이지 패턴)
  const heroCtaRef = useRef(null);
  const finalCtaSectionRef = useRef(null);
  const footerRef = useRef(null);
  const [heroCtaInView, setHeroCtaInView] = useState(true);
  const [finalCtaInView, setFinalCtaInView] = useState(false);
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observers = [];
    const watch = (node, setter, threshold) => {
      if (!node) return;
      const observer = new IntersectionObserver(
        ([entry]) => setter(Boolean(entry?.isIntersecting)),
        { threshold }
      );
      observer.observe(node);
      observers.push(observer);
    };
    watch(heroCtaRef.current, setHeroCtaInView, 0.35);
    watch(finalCtaSectionRef.current, setFinalCtaInView, 0.15);
    watch(footerRef.current, setFooterInView, 0.1);
    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  // 동적 통계 상태
  const [stats, setStats] = useState(null);
  const [displayStats, setDisplayStats] = useState({
    teachers: 15000,     // 로딩 중 기본값
    saenggibu: 5000,     // 로딩 중 기본값
    inspections: 10000,  // 로딩 중 기본값
    proUsers: 10000,     // 로딩 중 기본값
    timeSaved: 500       // 로딩 중 기본값
  });

  // 통계 API 호출
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(getPublicStatsUrl());
        setStats(response.data);
      } catch (error) {
        console.error('통계 조회 실패:', error);
        // 폴백 데이터
        setStats({
          total_teachers: 15000,
          total_saenggibu_created: 5000,
          total_inspections: 10000,
          total_pro_users: 10000,
          time_saved_minutes: 35000,
          time_saved_hours: 583,
          time_saved_days: 24.3,
          hourly_increase: {
            teachers: 5,
            saenggibu: 20,
            inspections: 50,
            pro_users: 2
          }
        });
      }
    };
    
    fetchStats();
  }, []);

  // 시간 비례 증가 애니메이션 - 뒷자리가 느릿느릿 계속 움직임
  useEffect(() => {
    if (!stats) {
      // API 로드 전: 로딩 애니메이션 (기본값에서 조금씩 증가)
      const interval = setInterval(() => {
        setDisplayStats(prev => ({
          teachers: prev.teachers + Math.floor(Math.random() * 2),     // 0~1 증가
          saenggibu: prev.saenggibu + Math.floor(Math.random() * 3),   // 0~2 증가
          inspections: prev.inspections + Math.floor(Math.random() * 5), // 0~4 증가
          proUsers: prev.proUsers + Math.floor(Math.random() * 2),     // 0~1 증가
          timeSaved: prev.timeSaved + Math.floor(Math.random() * 2)    // 0~1 증가
        }));
      }, 2000);
      
      return () => clearInterval(interval);
    }
    
    // API 로드 후: 실제 데이터 기반으로 증가
    // 🛡️ 방어 코드: hourly_increase가 없을 경우 기본값 사용
    const hourlyIncrease = stats.hourly_increase || {
      teachers: 5,
      saenggibu: 20,
      inspections: 50,
      pro_users: 2
    };
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = (now % 3600000) / 3600000; // 현재 시간의 경과 비율 (0~1)
      
      // 실제 증가량 기반으로 계산 (구라 없음)
      const actualIncrease = {
        teachers: (hourlyIncrease.teachers || 5) * elapsed,
        saenggibu: (hourlyIncrease.saenggibu || 20) * elapsed,
        inspections: (hourlyIncrease.inspections || 50) * elapsed,
        proUsers: (hourlyIncrease.pro_users || 2) * elapsed
      };
      
      setDisplayStats({
        teachers: Math.floor((stats.total_teachers || 15000) + actualIncrease.teachers),
        saenggibu: Math.floor((stats.total_saenggibu_created || 5000) + actualIncrease.saenggibu),
        inspections: Math.floor((stats.total_inspections || 10000) + actualIncrease.inspections),
        proUsers: Math.floor((stats.total_pro_users || 10000) + actualIncrease.proUsers),
        timeSaved: Math.floor((stats.time_saved_days || 24.3) + ((actualIncrease.saenggibu * 7) / (24 * 60))) // 7분으로 변경
      });
    }, 2000); // 2초마다 업데이트 - 뒷자리가 느릿느릿 변함
    
    return () => clearInterval(interval);
  }, [stats]);

  // 스크롤 애니메이션 — AI30 검증 패턴 공용 훅 (reduced-motion·IO 폴백 포함)
  useScrollReveal(null, '.animate-section, .animate-card', 'visible');

  // ── 검사기 데모 ──
  const inspRef = useRef(null);
  const [inspPhase, setInspPhase] = useState(0);
  const [inspHlCount, setInspHlCount] = useState(0);
  const [inspDetailIdx, setInspDetailIdx] = useState(0);

  useEffect(() => {
    const el = inspRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && inspPhase === 0) setInspPhase(1); },
      { threshold: 0.75 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inspPhase]);

  useEffect(() => {
    if (inspPhase === 1) {
      const t = setTimeout(() => setInspPhase(2), 600);
      return () => clearTimeout(t);
    }
    if (inspPhase === 2) {
      const t = setTimeout(() => setInspPhase(3), 600);
      return () => clearTimeout(t);
    }
    if (inspPhase === 3) {
      const t = setTimeout(() => setInspPhase(4), 1000);
      return () => clearTimeout(t);
    }
    if (inspPhase === 4) {
      let c = 0;
      const t = setInterval(() => {
        c++;
        setInspHlCount(c);
        if (c >= INSP_VIOLATIONS.length) {
          clearInterval(t);
          setTimeout(() => setInspPhase(5), 700);
        }
      }, 350);
      return () => clearInterval(t);
    }
  }, [inspPhase]);

  useEffect(() => {
    if (inspPhase < 5) return;
    const t = setInterval(() => {
      setInspDetailIdx(prev => (prev + 1) % INSP_VIOLATIONS.length);
    }, 3000);
    return () => clearInterval(t);
  }, [inspPhase]);


  const handleDemoCta = (feature, path, location, state) => {
    trackABTestEvent('landing_cta_button', 'cta_click', { location });
    trackDemoCta('home', feature, path);
    navigate(path, state ? { state } : undefined);
  };


  const navigateToPath = (path, requireAuth) => {
    if (requireAuth && !currentUser) {
      navigate('/login', { state: { from: path } });
      return;
    }
    navigate(path);
  };

  const proceedSpring14Signup = useCallback(() => {
    clearPendingFreeTrial();
    localStorage.setItem('pending_coupon_code', SPRING14_COUPON_CODE);
    setShowSpring14Guide(false);
    navigate(`/auth?tab=signup&redirect=${encodeURIComponent('/home')}`);
  }, [navigate]);

  const handleSpring14CouponStart = useCallback(async (locationLabel) => {
    trackABTestEvent('landing_cta_button', 'cta_click', {
      location: locationLabel,
      coupon: SPRING14_COUPON_CODE,
    });

    // SPRING14는 14일 쿠폰 claim 흐름이다. 기존 7일 free-trial pending과 동시에
    // 남아 있으면 인증 후 두 혜택이 경합할 수 있어 명시적으로 제거한다.
    clearPendingFreeTrial();

    if (!currentUser) {
      setShowSpring14Guide(true);
      return;
    }

    if (spring14ApplyingRef.current) return;
    spring14ApplyingRef.current = true;
    setIsSpring14Applying(true);
    showToast('14일 PRO 혜택을 적용하고 있어요.', 'info', 2500);

    try {
      const result = await claimCoupon(SPRING14_COUPON_CODE, currentUser, {
        source: 'home_coupon_query',
        trigger_location: locationLabel,
        url: window.location.href,
      });
      localStorage.removeItem('pending_coupon_code');
      await fetchSubscriptionStatus(true);

      const benefitDays = result?.benefit_days || 14;
      const message = result?.already_claimed
        ? '이미 14일 PRO 혜택이 적용되어 있어요. 바로 이용해보세요.'
        : `${benefitDays}일 PRO 혜택이 시작됐어요. 바로 이용해보세요.`;
      showToast(message, 'success', 5000);
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('SPRING14 쿠폰 적용 실패:', error);
      showToast(getCouponErrorMessage(error), 'error', 5000);
    } finally {
      spring14ApplyingRef.current = false;
      setIsSpring14Applying(false);
    }
  }, [currentUser, fetchSubscriptionStatus, navigate, showToast]);

  // 🎯 SEO 메타데이터: 랜딩페이지 vs 홈페이지 차별화
  const seoData = isLandingPage ? {
    title: "교사 전용 AI 올인원 플랫폼 | 생기부 3분 · PPT · 학습지 · 이미지 | 왓퀴즈",
    description: `생기부 3분 완성, AI PPT 만들기, 학습지 자동 생성, 교육용 이미지 생성, 수행평가 채점까지. 현직 교사 ${TEACHER_COUNT_LABEL}이 검증한 교사 전용 AI. 2026 기재요령 반영. 학교 예산 결제 가능.`,
    keywords: "교사 AI, 생기부 AI, AI PPT 만들기, 학습지 만들기, AI 이미지 생성, 교사 전용 AI, 수행평가 채점, 교사 업무 자동화, 교육 AI 플랫폼, 왓퀴즈",
    ogUrl: "https://whatquiz.co.kr"
  } : {
    title: "교사 전용 AI 올인원 | 생기부 · PPT · 학습지 · 이미지 · 채점 | 왓퀴즈",
    description: `생기부 AI 자동 작성, PPT 만들기, 학습지 생성, 이미지 생성, 수행평가 채점까지 올인원. 현직 교사 ${TEACHER_COUNT_LABEL}이 선택한 교사 전용 AI 플랫폼. 무료로 시작하세요.`,
    keywords: "생기부 AI 자동 작성, AI PPT 만들기, 학습지 자동 생성, AI 이미지 생성, 수행평가 채점, 교사 업무 자동화, 교육 AI 플랫폼, 세특 AI 생성, 교사 AI 도구",
    ogUrl: "https://whatquiz.co.kr/home"
  };

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="플랙티컬 주식회사" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoData.ogUrl} />
        <meta property="og:site_name" content="왓퀴즈" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="왓퀴즈 - 교사 전용 AI 올인원 플랫폼" />
        <meta property="og:locale" content="ko_KR" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@whatquiz" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        
        {/* 모바일 */}
        <meta name="apple-mobile-web-app-title" content="왓퀴즈" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="mobile-web-app-capable" content="yes" />
        
        <link rel="canonical" href={seoData.ogUrl} />
        
        {/* 구조화된 데이터 - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "왓퀴즈",
            "description": seoData.description,
            "url": seoData.ogUrl,
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web",
            "author": {
              "@type": "Organization",
              "name": "플랙티컬 주식회사",
              "url": "https://whatquiz.co.kr"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "KRW",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "bestRating": "5",
              "worstRating": "1",
              "ratingCount": "1247"
            },
            "featureList": [
              "생기부 AI 자동 작성",
              "학습지 자동 생성",
              "수행평가 설계 도구",
              "AI 교사 상담",
              "개인정보 보호",
              "무료로 시작"
            ],
            "keywords": seoData.keywords
          })}
        </script>
      </Helmet>
      <style>{tossStyleHomepage}</style>
      
      {/* 배너 제거 → 히어로 하단 신뢰 라인에 흡수 */}
      
      <div style={{ fontFamily: 'var(--whatquiz-font-family)', scrollSnapType: 'y proximity' }}>
        {/* 🧪 A/B 테스트 적용 히어로 섹션 - 강력하고 명확한 가치 제안 */}
        <section className="toss-hero animate-section">
          <h1 className="toss-hero-title">
            생기부,<br />
            <span style={{color: 'var(--whatquiz-primary)'}}>3분이면 돼요</span>
          </h1>
          <p className="toss-hero-subtitle">
            7시간 걸리던 생기부, 왓퀴즈가 3분으로 줄여 드려요.
            {displayStats.teachers > 0 && (
              <>
                <br />
                <strong style={{ color: 'var(--whatquiz-primary)' }}>
                  <CountUp
                    start={0}
                    end={displayStats.teachers}
                    duration={2.5}
                    separator=","
                  />명
                </strong> 선생님이 이미 사용 중이에요.
              </>
            )}
          </p>
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'}}>
            <button 
              ref={heroCtaRef}
              className="toss-hero-cta"
              onClick={() => {
                if (isSpring14CouponMode) {
                  handleSpring14CouponStart('hero_spring14');
                  return;
                }
                trackABTestEvent('landing_cta_button', 'cta_click', { location: 'hero' });
                navigateToPath('/create-saenggibu', false);
              }}
              disabled={isSpring14Applying}
            >
              {isSpring14CouponMode
                ? isSpring14Applying ? '14일 PRO 적용 중...' : '14일 PRO 무료로 받기'
                : '무료로 생기부 써보기'}
            </button>
          </div>
          
          {/* 신뢰 한 줄 */}
          <p style={{
            marginTop: '20px',
            fontSize: '13px',
            color: '#6b7684',
            letterSpacing: '0.01em',
            fontWeight: '500'
          }}>
            학교장터(S2B) 등록 · 에듀집 인증 · 학교 예산 결제 가능
          </p>
        </section>

        {/* 생기부 데모 */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px' }}>
          <div className="home-demo-solo">
            <SaenggibuResultDemo surface="home" />
          </div>
          <div className="home-demo-cta">
            <button
              type="button"
              className="demo-cta-btn"
              onClick={() => handleDemoCta('saenggibu', '/create-saenggibu', 'demo')}
            >
              직접 만들어보기 →
            </button>
          </div>
        </section>

        {/* 반 전체 데모 */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px', scrollSnapAlign: 'start' }}>
          <div className="demo-row">
            <div className="demo-text-side">
              <div className="demo-text-title">28명도,<br/>겹치지 않게 한 번에</div>
              <p className="demo-text-desc">명단을 넣으면 반 전체를 한 번에 쓰고, 다른 학생과 겹치는 문장은 골라서 고쳐요.</p>
              <button
                type="button"
                className="home-demo-link"
                onClick={() => handleDemoCta('class_flow', '/create-saenggibu', 'demo_class', { startBatch: true })}
              >
                반 전체 써보기 →
              </button>
            </div>
            <div className="demo-mockup-side">
              <ClassFlowDemo surface="home" />
            </div>
          </div>
        </section>

        {/* 검사기 데모 섹션 */}
        <section ref={inspRef} className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px', scrollSnapAlign: 'start' }}>
          <div className="demo-row">
            <div className="demo-text-side">
              <div className="demo-text-title">제출 전,<br/>한 번 더 봐줄게요</div>
              <p className="demo-text-desc">기재요령 원문과 대조해서, 출처까지 알려드려요.</p>
              <button
                type="button"
                className="home-demo-link"
                onClick={() => handleDemoCta('inspector', '/saenggibu-inspector', 'demo_inspector')}
              >
                검사해 보기 →
              </button>
            </div>
            <div className="demo-mockup-side">
          <div className="demo-mockup">
            <div className="demo-mockup-bar">
              <span className="demo-mockup-title">생기부 검사기</span>
            </div>
            <div className="demo-mockup-body">
              <div className="demo-insp-text">
                {INSP_SEGS.map((seg, i) =>
                  seg.hi !== undefined ? (
                    <span
                      key={i}
                      className={`demo-hl ${INSP_VIOLATIONS[seg.hi].cat}${inspHlCount > seg.hi ? ' on' : ''}${inspPhase >= 5 && seg.hi === inspDetailIdx ? ' sel' : ''}`}
                    >{seg.t}</span>
                  ) : (
                    <span key={i}>{seg.t}</span>
                  )
                )}
              </div>

              {inspPhase >= 1 && (
                <div className="demo-btn-row">
                  <button
                    className={`demo-gen-btn${inspPhase === 2 ? ' pressed' : ''}`}
                    style={{ pointerEvents: inspPhase >= 5 ? 'auto' : 'none', cursor: inspPhase >= 5 ? 'pointer' : 'default', opacity: inspPhase >= 3 && inspPhase < 5 ? 0 : 1, transition: 'opacity 0.3s ease' }}
                    onClick={() => navigateToPath('/saenggibu-inspector', false)}
                  >검사하기</button>
                </div>
              )}

              {inspPhase === 3 && (
                <div className="demo-loading-dots">
                  <span className="demo-loading-dot" />
                  <span className="demo-loading-dot" />
                  <span className="demo-loading-dot" />
                </div>
              )}

              <div className={`demo-insp-summary${inspPhase >= 5 ? ' show' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                개선 권장: {INSP_VIOLATIONS.length}개
              </div>

              {inspPhase >= 5 && (
                <div className="demo-insp-detail" key={inspDetailIdx}>
                  <div className="demo-insp-detail-head">
                    <span className="demo-insp-cat" style={{ background: INSP_VIOLATIONS[inspDetailIdx].color }}>
                      {INSP_VIOLATIONS[inspDetailIdx].type}
                    </span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{inspDetailIdx + 1} / {INSP_VIOLATIONS.length}</span>
                  </div>

                  <div className="demo-insp-fix">
                    <span className="demo-insp-fix-from">{INSP_VIOLATIONS[inspDetailIdx].found}</span>
                    <span style={{ color: '#d1d5db' }}>→</span>
                    <span className="demo-insp-fix-to">{INSP_VIOLATIONS[inspDetailIdx].suggestion}</span>
                  </div>

                  <div className="demo-insp-reason">
                    {INSP_VIOLATIONS[inspDetailIdx].reason}
                  </div>

                  <div className="demo-insp-source">
                    <strong>{INSP_VIOLATIONS[inspDetailIdx].source}</strong>
                    "{INSP_VIOLATIONS[inspDetailIdx].quote}"
                  </div>
                </div>
              )}
            </div>
          </div>
            </div>
          </div>
        </section>

        {/* 브랜드 메시지 */}
        <div className="animate-section" style={{ textAlign: 'center', padding: '20px 20px 8px' }}>
          <p style={{ fontSize: '18px', fontWeight: '600', color: '#4e5968', margin: 0, fontFamily: 'var(--whatquiz-font-family)' }}>
            복잡한 건, 왓퀴즈가 할게요
          </p>
        </div>

        {/* 채점 데모 섹션 */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px', scrollSnapAlign: 'start' }}>
          <div className="demo-row demo-row-reverse">
            <div className="demo-text-side">
              <div className="demo-text-title">30명 채점,<br/>커피 한 잔이면 끝나요</div>
              <p className="demo-text-desc">스캔한 답안 PDF를 올리면 학생별로 나누고, 점수 근거를 답안 위에 표시해요.</p>
              <button
                type="button"
                className="home-demo-link"
                onClick={() => handleDemoCta('scoring_scan', '/scoring', 'demo_scoring')}
              >
                채점해 보기 →
              </button>
            </div>
            <div className="demo-mockup-side">
              <ScoringScanDemo surface="home" />
            </div>
          </div>
        </section>

        {/* AI 한글(HWP) 작성 데모 — HwpDemoSection 컴포넌트로 추출.
            좌측 채팅창 (양식 첨부 + 사용자 메시지 → AI 응답) +
            우측 한글 양식 패널 (셀이 차례로 채워지는 애니메이션).
            ad-pro 페이지 등 다른 랜딩에서도 동일 컴포넌트 재사용. */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px', scrollSnapAlign: 'start' }}>
          <HwpDemoSection trackingLocation="demo_hwp_home" />
        </section>

        {/* 수업 준비: PPT 바로 고치기 + 3종 */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px', scrollSnapAlign: 'start' }}>
          <div className="demo-row">
            <div className="demo-text-side">
              <div className="demo-text-title">수업 준비,<br/>한 번에 끝내요</div>
              <p className="demo-text-desc">PPT·학습지·이미지를 만들고, 만든 PPT는 글자를 눌러 바로 고쳐요.</p>
            </div>
            <div className="demo-mockup-side">
              <PptEditDemo surface="home" />
            </div>
          </div>
          <div
            className="home-prep-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '16px',
              maxWidth: '860px',
              margin: '32px auto 0',
              padding: '0 20px',
            }}
          >
            {[
              {
                feature: 'ppt',
                title: '수업 PPT',
                desc: '주제만 말하면 완성, 글자는 눌러서 고쳐요',
                img: '/examples/ppt/ppt-solar-system.webp',
                path: '/chat?category=ppt_maker',
              },
              {
                feature: 'worksheet',
                title: '학습지',
                desc: '문제에 맞는 그래프·그림까지 넣어요',
                img: '/images/help/update-2026-10-figures/ws-climate.jpg',
                imgPosition: 'top',
                path: '/chat?category=worksheet_gen',
              },
              {
                feature: 'image',
                title: '이미지',
                desc: '교육용 삽화·다이어그램·색칠 도안',
                img: '/examples/images/img-water-cycle.webp',
                path: '/chat?category=image_generation',
              },
            ].map((item) => (
              <button
                key={item.feature}
                type="button"
                onClick={() => handleDemoCta(item.feature, item.path, `prep_${item.feature}`)}
                className="home-hover-lift"
                style={{
                  display: 'block',
                  padding: 0,
                  border: 'none',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  background: '#f8f9fa',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                }}
              >
                <div style={{ width: '100%', aspectRatio: '16/10', overflow: 'hidden', background: '#e5e7eb' }}>
                  <img src={item.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: item.imgPosition || 'center' }} loading="lazy" />
                </div>
                <div className="home-prep-card-body" style={{ padding: '16px 18px 20px' }}>
                  <div className="home-prep-card-title" style={{ fontSize: '16px', fontWeight: '700', color: '#191f28', marginBottom: '4px' }}>{item.title}</div>
                  <p className="home-prep-card-desc" style={{ fontSize: '14px', color: '#6b7684', margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* AI 채팅 데모 */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '48px', scrollSnapAlign: 'start' }}>
          <div className="demo-row demo-row-reverse">
            <div className="demo-text-side">
              <div className="demo-text-title">궁금한 건,<br/>표와 그래프로 바로</div>
              <p className="demo-text-desc">GPT·Claude·Gemini 최신 모델을 한곳에서 써요. 답을 표와 그래프로 정리하고, 소리로도 읽어 줘요.</p>
              <button
                type="button"
                className="home-demo-link"
                onClick={() => handleDemoCta('chat', '/chat', 'demo_chat')}
              >
                AI 채팅 써보기 →
              </button>
            </div>
            <div className="demo-mockup-side">
              <ChatAnswerDemo surface="home" />
            </div>
          </div>
        </section>

        {/* 앰버서더 선생님 섹션 — 공식 위촉의 위엄 (딥 네이비 + 골드 액센트) */}
        <section className="toss-section animate-section home-ambassador-section" style={{ 
          paddingTop: '40px', 
          paddingBottom: '40px'
        }}>
          <p className="home-ambassador-section__eyebrow">
            앰버서더 {AMBASSADOR_GENERATION}기 · 왓퀴즈 공식 위촉 {AMBASSADORS.length}명
          </p>
          <h2 className="toss-section-title">
            왓퀴즈 공식 앰버서더 {AMBASSADOR_GENERATION}기
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '15px',
            color: '#6b7280',
            marginTop: '-4px',
            marginBottom: '20px',
            fontWeight: '400',
            wordBreak: 'keep-all',
          }}>
            전국에서 위촉된 {AMBASSADORS.length}명의 선생님.<br />
            왓퀴즈가 가장 자랑스러워하는 이름들이에요
          </p>

          <div
            ref={ambassadorScrollRef}
            className="ambassador-scroll-container"
            onMouseEnter={stopAutoScroll}
            onMouseLeave={(e) => { handleDragEnd(); startAutoScroll(); }}
            onMouseDown={handleDragStart}
            onMouseUp={handleDragEnd}
            onMouseMove={handleDragMove}
            onTouchStart={stopAutoScroll}
            onTouchEnd={() => setTimeout(startAutoScroll, 5000)}
            style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '8px',
              maxWidth: '800px',
              margin: '0 auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              cursor: 'grab',
              userSelect: 'none',
            }}
          >
            {shuffledAmbassadors.map((amb) => (
              <div
                key={amb.id}
                className="home-ambassador-card"
                onMouseDown={handleCardMouseDown}
                onClick={() => handleCardClick(amb)}
              >
                <span className="home-ambassador-card__label">공식 앰버서더</span>
                <img
                  src={amb.avatar}
                  alt={`${amb.name} 선생님`}
                  loading="lazy"
                  className="home-ambassador-card__avatar"
                />
                <p className="home-ambassador-card__name">{amb.name} 선생님</p>
                <p className="home-ambassador-card__school">{amb.school}</p>
                <p className="home-ambassador-card__thanks">{amb.thanks}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PRO 결제 유도 섹션 */}
        <section className="toss-section animate-section" style={{ 
          paddingTop: '40px', 
          paddingBottom: '48px'
        }}>
          <h2 className="toss-section-title">
            {displayStats.proUsers > 0 ? (
              <>
                이미 <span style={{ color: 'var(--whatquiz-primary)' }}>
                  <CountUp
                    start={0}
                    end={displayStats.proUsers}
                    duration={2.5}
                    separator=","
                  />명
                </span>의 선생님이<br/>PRO를 경험하고 있어요
              </>
            ) : (
              <>이미 많은 선생님이<br/>PRO를 경험하고 있어요</>
            )}
          </h2>
          
          {/* 토스 스타일 - 하나의 깔끔한 카드 */}
          <div style={{
            maxWidth: '400px',
            margin: '32px auto 0',
            background: 'white',
            borderRadius: '20px',
            padding: '40px 32px',
            textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '800',
              color: 'var(--whatquiz-primary)',
              marginBottom: '8px',
              fontFamily: 'var(--whatquiz-font-family)'
            }}>
              월 16,667원부터
            </div>
            <div style={{
              fontSize: '15px',
              color: '#6b7280',
              marginBottom: '12px'
            }}>
              학교 예산 결제 가능
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              {[AI_MODELS.gpt.latest, AI_MODELS.claude.latest, AI_MODELS.gemini.latest].map(m => (
                <span key={m} style={{
                  padding: '4px 10px',
                  background: '#f0f7ff',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#3182f6'
                }}>{m}</span>
              ))}
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <button
                onClick={() => {
                  trackHomePreorderClick('pricing_section');
                  navigateToPath('/pricing', false);
                }}
                className="home-btn-primary"
                style={{
                  padding: '16px',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                PRO 시작하기
              </button>
              <button
                onClick={() => navigateToPath('/pricing', false)}
                style={{
                  padding: '14px',
                  background: 'transparent',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                요금제 자세히 보기
              </button>
            </div>
          </div>
          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#9ca3af' }}>
            7일 이내 100% 환불 · 카드 등록 없이 무료 체험
          </p>
        </section>

        {/* FAQ 섹션 */}
        <section className="toss-section animate-section">
          <h2 className="toss-section-title">자주 묻는 질문</h2>
          
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <FAQItem 
              question="학교 예산으로 결제할 수 있나요?"
              answer="네, 가능해요. 왓퀴즈는 학교장터(S2B)와 에듀집에 모두 등록되어 있어 학교 예산으로 구매할 수 있어요. 학운위 심의 대상이 아니라서 견적서와 품의서만으로 바로 진행하실 수 있어요."
              defaultOpen
            />
            <FAQItem 
              question="생기부가 다 비슷하게 나오지 않나요?"
              answer="파일 첨부 기능을 활용하시면 완전히 달라져요. 학생별 누가기록, 활동자료, 성적표 등을 첨부하시면 AI가 실제 데이터를 기반으로 작성하기 때문에 학생마다 개성 있는 문장이 나와요. 없는 내용을 지어내는 일도 크게 줄어요. 첨부한 자료에 있는 사실만 골라서 쓰거든요."
            />
            <FAQItem
              question="학생부 상업적 이용 제한 법이 시행되면 왓퀴즈를 써도 되나요?"
              answer={
                <div>
                  <p style={{ marginBottom: '12px' }}>
                    네. 이번 개정은 학생부를 취득해 입시 상담·유료 자료·AI 학습 데이터 등 영업 목적으로 거래하거나 이용하는 행위를 제한해요. 교사가 직접 관찰·평가한 자료로 기록 초안을 만들고 최종 내용을 확인하는 왓퀴즈의 통상적인 사용 방식은 교육부의 주요 위반 사례와 달라요.
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    왓퀴즈는 학생부를 매매하거나 제3자의 영업 목적으로 제공한 적이 없으며, 입력 자료로 AI를 학습시키지 않아요. 검사기에는 최종 확정본이 아닌 작성 중인 초안만 이용해 주세요.
                  </p>
                  <a href="/help/notice/saenggibu-commercial-use-law-2026" style={{ color: '#2563eb', fontWeight: 600 }}>
                    법 개정 대응 자세히 보기
                  </a>
                </div>
              }
            />
            <FAQItem 
              question="학운위 심의를 받아야 하나요?"
              answer={
                <div>
                  <p style={{ marginBottom: '16px' }}>
                    아니요. 한국교육학술정보원(KERIS)이 에듀집에 등록된 학습지원 소프트웨어를 전수 검토한 결과, 왓퀴즈는 학교운영위원회 심의 대상이 아닌 것으로 분류됐어요(2026. 9. 22. 에듀집 반영). 정규 교과 수업에 쓰지 않는 교사용·행정업무 지원 서비스이기 때문이에요. 견적서와 품의서로 바로 구매하시면 되고, 개인정보 보호 기준을 확인하실 수 있는 필수기준 체크리스트도 받아 보실 수 있어요.
                  </p>
                  <button
                    onClick={() => window.location.href = '/pricing#docs'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      background: 'var(--whatquiz-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    학교 구매 서류 보기
                  </button>
                </div>
              }
            />
            <FAQItem
              question="무료로 써볼 수 있나요?"
              answer={`네. 회원가입만 하면 AI 채팅, 생기부 검사 등 기본 기능을 무료로 사용할 수 있어요. PRO 기능(일괄 생성, 파일 첨부, GPT·Claude·Gemini 등 최신 AI 모델)은 시즌 한정 ${maxDiscountLabel} 받으실 수 있어요.`}
            />
          </div>
        </section>

        {/* 아침 교육 뉴스 진입 */}
        <section className="toss-section animate-section" style={{ paddingTop: '16px', paddingBottom: '64px' }}>
          <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            padding: '36px 28px',
            borderRadius: '20px',
            background: '#f6faff',
            textAlign: 'center'
          }}>
            <span style={{
              display: 'inline-block',
              marginBottom: '12px',
              padding: '6px 12px',
              borderRadius: '999px',
              background: '#e8f3ff',
              color: '#3182f6',
              fontSize: '12px',
              fontWeight: '800'
            }}>
              매일 아침 8시
            </span>
            <h2 style={{
              margin: '0 0 10px',
              color: '#191f28',
              fontSize: '28px',
              fontWeight: '800',
              lineHeight: '1.35'
            }}>
              출근길 3분이면<br />오늘 교육 소식 끝
            </h2>
            <p style={{
              margin: '0 0 22px',
              color: '#6b7684',
              fontSize: '15px',
              lineHeight: '1.6'
            }}>
              교육 정책, 학교 현장, 해외 소식을 짧게 정리해 드려요. 메일로도 받아 볼 수 있어요.
            </p>
            <button
              onClick={() => navigateToPath('/news', false)}
              style={{
                minHeight: '50px',
                padding: '13px 22px',
                border: 0,
                borderRadius: '12px',
                background: '#3182f6',
                color: '#fff',
                fontSize: '15px',
                fontWeight: '800',
                cursor: 'pointer'
              }}
            >
              오늘 뉴스 보기
            </button>
          </div>
        </section>

        {/* 마무리 CTA 섹션 - pricing 스타일 */}
        <section ref={finalCtaSectionRef} style={{
          padding: '80px 20px',
          background: '#e8f3ff',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '700',
              lineHeight: '1.3',
              margin: '0 0 16px',
              color: '#191f28',
              fontFamily: 'var(--whatquiz-font-family)'
            }}>
              왓퀴즈니까,<br />오늘 퇴근 3시간 빨라질 수 있어요
            </h2>
            <p style={{
              fontSize: '15px',
              color: '#6b7684',
              margin: '0 0 40px'
            }}>
              가입 없이 바로 체험 · 7일 이내 100% 환불
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              maxWidth: '320px',
              margin: '0 auto'
            }}>
              {isSpring14CouponMode ? (
                <button
                  onClick={() => handleSpring14CouponStart('final_spring14')}
                  disabled={isSpring14Applying}
                  className="home-btn-primary home-btn-primary--lift"
                  style={{
                    padding: '18px 32px',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '17px',
                    fontWeight: '700',
                    cursor: isSpring14Applying ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                >
                  {isSpring14Applying ? '14일 PRO 적용 중...' : '14일 PRO 무료로 받기'}
                </button>
              ) : (
                <FreeTrialCTAButton
                  variant="home-final"
                  triggerFeature="home_final_cta"
                  triggerPage="/"
                  onAlreadyUsed={
                    <button
                      onClick={() => {
                        trackABTestEvent('landing_cta_button', 'cta_click', { location: 'final' });
                        navigateToPath('/create-saenggibu', false);
                      }}
                      className="home-btn-primary home-btn-primary--lift"
                      style={{
                        padding: '18px 32px',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '14px',
                        fontSize: '17px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        width: '100%'
                      }}
                    >
                      무료로 생기부 써보기
                    </button>
                  }
                />
              )}
              {/* Secondary: 즉시 체험 (가입 없이) — 후퇴된 위계 */}
              <button
                onClick={() => {
                  trackABTestEvent('landing_cta_button', 'cta_click', { location: 'final_secondary' });
                  navigateToPath('/create-saenggibu', false);
                }}
                className="home-btn-soft"
                style={{
                  padding: '14px 28px',
                  color: '#191f28',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                가입 없이 생기부 먼저 써보기
              </button>
              {/* Ghost: 요금제 보기 */}
              <button
                onClick={() => navigateToPath('/pricing', false)}
                className="home-btn-ghost"
                style={{
                  padding: '10px 32px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                요금제 보기
              </button>
            </div>
          </div>
        </section>
      </div>
      
      {/* PRO 무료체험 모달 */}
      <ProUpgradeModal 
        isOpen={showProModal}
        onClose={() => setShowProModal(false)}
        feature="상위 모델"
        onTrialStart={() => {
          setShowProModal(false);
          navigate('/chat');
        }}
      />

      {/* SPRING14 쿠폰 안내 모달 — Dialog 프리미티브 */}
      <Dialog
        align="center"
        description="회원가입만 하면 SPRING14 쿠폰이 자동 등록돼요. 카드 등록이나 자동 결제는 없어요."
        footer={(
          <ModalActions
            primary={{ label: '회원가입하고 14일 PRO 받기', onClick: proceedSpring14Signup }}
            tertiary={{ label: '조금 더 둘러볼게요', onClick: () => setShowSpring14Guide(false) }}
            testId="home-coupon-modal-actions"
          />
        )}
        icon={(
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#e8f3ff',
              color: '#3182f6'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 12v10H4V12" />
              <path d="M2 7h20v5H2z" />
              <path d="M12 22V7" />
              <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z" />
              <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z" />
            </svg>
          </span>
        )}
        onClose={() => setShowSpring14Guide(false)}
        open={showSpring14Guide}
        size="sm"
        testId="home-coupon-modal"
        title="14일 PRO 혜택을 자동으로 적용해드릴게요"
      >
        <ul className="home-coupon-modal__perks">
          {['생기부 일괄 생성과 파일 첨부 사용', 'GPT·Claude·Gemini 최신 모델 이용', '가입 후 홈으로 바로 돌아오기'].map((item) => (
            <li key={item}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M13.333 4L6 11.333 2.667 8" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Dialog>
      
      {/* 앰버서더 소개 모달 — Dialog 프리미티브 (콘페티·회전 링 없이 차분하게) */}
      <Dialog
        align="center"
        aria-label={selectedAmbassador ? `${selectedAmbassador.name} 선생님 소개` : '앰버서더 소개'}
        onClose={() => setSelectedAmbassador(null)}
        open={Boolean(selectedAmbassador)}
        size="sm"
        testId="home-ambassador-modal"
      >
        {selectedAmbassador && (
          <div className="home-ambassador-modal" style={{ textAlign: 'center' }}>
            {/* 위촉장 구조 (배지 + 타이틀), 톤은 기존 모달과 동일 */}
            <span className="home-ambassador-modal__badge">AMBASSADOR {AMBASSADOR_GENERATION}기</span>
            <p className="home-ambassador-modal__title">왓퀴즈 공식 앰버서더 제{AMBASSADOR_GENERATION}기</p>

            <img
              alt={`${selectedAmbassador.name} 선생님`}
              className="home-ambassador-modal__avatar"
              src={selectedAmbassador.avatar}
            />
            <p className="home-ambassador-modal__name">{selectedAmbassador.name} 선생님</p>
            <p className="home-ambassador-modal__school">{selectedAmbassador.school}</p>

            <hr className="home-ambassador-modal__rule" />
            <p className="home-ambassador-modal__appoint">
              위 선생님을 왓퀴즈 공식 앰버서더 {AMBASSADOR_GENERATION}기로 위촉합니다
            </p>

            <span className="home-ambassador-modal__label">왓퀴즈 팀이 드리는 글</span>
            <div className="home-ambassador-modal__thanks">
              {buildAmbassadorLetter(selectedAmbassador.name, AMBASSADORS.length).map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
              <small>{AMBASSADOR_APPOINT_YEAR} · 왓퀴즈 팀 드림</small>
            </div>
          </div>
        )}
      </Dialog>

      {/* 모바일 스티키 하단 CTA — 히어로 primary와 동일한 가치·행동 */}
      <div className={`home-sticky-cta${heroCtaInView || finalCtaInView || footerInView ? ' is-hidden' : ''}`}>
        <button
          type="button"
          className="home-sticky-cta-btn"
          disabled={isSpring14Applying}
          onClick={() => {
            if (isSpring14CouponMode) {
              handleSpring14CouponStart('sticky_spring14');
              return;
            }
            trackABTestEvent('landing_cta_button', 'cta_click', { location: 'sticky' });
            navigateToPath('/create-saenggibu', false);
          }}
        >
          {isSpring14CouponMode
            ? isSpring14Applying ? '14일 PRO 적용 중...' : '14일 PRO 무료로 받기'
            : '무료로 생기부 써보기'}
        </button>
      </div>

      <div ref={footerRef}>
        <Footer />
      </div>
    </>
  );
};

// FAQ 아이템 컴포넌트 - 토스 스타일: border 없음, 배경색 구분
// 4050 가이드: 첫 항목을 기본 펼침해 "펼칠 수 있구나" 패턴 학습을 유도한다
const FAQItem = ({ question, answer, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{
      background: isOpen ? '#f0f4ff' : '#f8f9fa',
      border: 'none',
      borderRadius: '16px',
      marginBottom: '10px',
      overflow: 'hidden',
      transition: 'all 0.2s ease'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '18px 20px',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '16px',
          fontWeight: '600',
          color: '#111827',
          fontFamily: 'var(--whatquiz-font-family)',
          transition: 'all 0.15s ease'
        }}
      >
        <span>{question}</span>
        <svg 
          width="16" height="16" viewBox="0 0 24 24" 
          fill="none" stroke="#9ca3af" strokeWidth="2.5" 
          strokeLinecap="round" strokeLinejoin="round"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
            marginLeft: '12px'
          }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      
      {isOpen && (
        <div style={{
          padding: '0 20px 18px',
          fontSize: '15px',
          lineHeight: '1.7',
          color: '#4b5563',
          animation: 'fadeIn 0.2s ease'
        }}>
          {typeof answer === 'string' ? answer : answer}
          {children}
        </div>
      )}
    </div>
  );
};

export default HomePage; 